import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { formatCountdownDisplay } from "@/lib/utils";

export default function ReportingVerifyStep({
  channel = "sms",
  otpCode = "",
  setOtpCode,
  otpError = "",
  setOtpError,
  resendCountdown = 0,
  sendingOtp = false,
  verifying = false,
  onSendOtp,
  onVerifyOtp,
  onBackToChannel,
  onExit,
}) {
  const handleSubmit = (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (!otpCode.trim()) {
      setOtpError("Please enter the verification OTP code.");
      return;
    }
    onVerifyOtp(otpCode.trim());
  };

  return (
    <Card className="bg-transparent border-0 text-white w-full mx-auto shadow-none">
      <CardHeader className="flex flex-col justify-center items-center text-center p-0 pb-3">
        <div className="w-14 h-14 rounded-full bg-cyellow/10 border border-cyellow/30 flex items-center justify-center mb-3 text-cyellow shadow-lg shadow-cyellow/5">
          <Lock className="w-7 h-7" />
        </div>
        <CardTitle className="text-xl font-bold text-white">
          Enter Verification Code
        </CardTitle>
        <CardDescription className="text-cwhite3 mt-1.5 text-sm leading-relaxed max-w-sm">
          Please enter the 6-digit verification code sent via{" "}
          <span className="text-cyellow font-semibold uppercase">
            {channel}
          </span>{" "}
          to unlock the reporting module.
        </CardDescription>
      </CardHeader>

      <form onSubmit={handleSubmit} className="space-y-4 pt-2">
        <div className="space-y-2 text-left">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-medium text-neutral-300">
              Enter OTP Code <span className="text-red-500">*</span>
            </Label>
            <button
              type="button"
              onClick={onBackToChannel}
              disabled={verifying || sendingOtp}
              className="text-xs text-neutral-400 hover:text-white flex items-center gap-1 cursor-pointer transition-colors"
            >
              <ArrowLeft size={12} /> Change Channel
            </button>
          </div>

          <Input
            type="text"
            placeholder="• • • • • •"
            value={otpCode}
            onChange={(e) => {
              setOtpCode(e.target.value);
              if (otpError) setOtpError("");
            }}
            autoFocus
            className={`bg-neutral-900 text-white font-mono tracking-widest text-center text-xl h-12 transition-all ${
              otpError
                ? "border-red-500 focus-visible:ring-red-500"
                : "border-neutral-700 focus-visible:ring-cyellow"
            }`}
          />

          {/* Inline OTP error message */}
          {otpError && (
            <div className="flex items-center gap-2 text-xs text-red-400 bg-red-950/40 border border-red-800/50 rounded-lg p-2.5 mt-1 animate-fadeIn">
              <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
              <span className="leading-snug">{otpError}</span>
            </div>
          )}

          <div className="flex justify-between items-center text-xs pt-1">
            <span className="text-neutral-400">Didn't receive code?</span>
            <button
              type="button"
              onClick={onSendOtp}
              disabled={verifying || sendingOtp || resendCountdown > 0}
              className={`font-medium transition-colors ${
                resendCountdown > 0 || sendingOtp
                  ? "text-neutral-500 cursor-not-allowed"
                  : "text-cyellow hover:underline cursor-pointer"
              }`}
            >
              {sendingOtp
                ? "Sending..."
                : resendCountdown > 0
                ? `Resend OTP (${formatCountdownDisplay(resendCountdown)})`
                : "Resend OTP"}
            </button>
          </div>
        </div>

        <CardContent className="p-0 pt-2">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onExit}
              disabled={verifying}
              className="w-full border-neutral-700 text-cwhite hover:bg-white/10 hover:text-white"
            >
              Exit
            </Button>
            <Button
              type="submit"
              disabled={verifying || !otpCode.trim()}
              className="w-full bg-cyellow hover:bg-yellow-500 text-black font-semibold flex items-center justify-center gap-2 shadow-lg shadow-cyellow/20 disabled:opacity-50"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Verifying...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Verify & Access</span>
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </form>
    </Card>
  );
}
